import { useState } from 'react';
import { Link } from 'react-router';
import {
  externalMembershipLabel,
  formatInteger,
  statisticsPeriodLabels,
  statisticsPeriods,
  type StatisticsPeriod,
} from '@brimo/shared';
import { Pill, Segmented, Select, Skeleton } from '../../../ds/components';
import { useStatistics, type Statistics } from '../api';
import { LoadError } from '../ui';
import styles from './statistics.module.css';

/** Bar width in % of the row's basis (the frame's two denominators: dials for „Nicht erreicht", else talks). */
function barWidth(row: Statistics['results'][number], kpis: Statistics['kpis']): number {
  const basis = row.basis === 'dials' ? kpis.dials : kpis.conversations;
  if (basis > 0) return Math.min(100, (row.count / basis) * 100);
  return Math.min(100, Math.max(0, row.pct));
}

/**
 * Controlling 05 — Setter-Statistik (Dialer › Statistik): own figures of the week or the month, five KPI
 * tiles, „Ergebnisse je Gespräch" as bars (basis per row as the API says), „Je Liste" and the note about
 * team figures; with leadership rights a select switches to another setter.
 */
export function StatisticsPage() {
  const [period, setPeriod] = useState<StatisticsPeriod>('month');
  const [userId, setUserId] = useState<string | null>(null);
  const statistics = useStatistics(period, userId);
  const data = statistics.data;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <div className={styles.crumb}>
            <Link to="/dialer" className={styles.crumbLink}>
              Dialer
            </Link>
          </div>
          <h1 className={styles.title}>Meine Statistik</h1>
          <div className={styles.subtitle}>
            {data ? (
              <>
                {`${data.userName} · ${data.periodLabel}`}
                {data.userExternalCompany ? (
                  <Pill className={styles.external}>{externalMembershipLabel(data.userExternalCompany)}</Pill>
                ) : null}
              </>
            ) : (
              <Skeleton width={180} height={13} />
            )}
          </div>
        </div>
        <div className={styles.headerEnd}>
          {data?.canSeeTeam && data.users.length > 1 ? (
            <Select
              label="Setter"
              labelHidden
              size="compact"
              value={userId ?? data.userId}
              options={data.users.map((user) => ({
                value: user.id,
                label: user.externalCompany
                  ? `${user.name} · ${externalMembershipLabel(user.externalCompany)}`
                  : user.name,
              }))}
              onChange={(event) => setUserId(event.target.value)}
            />
          ) : null}
          <Segmented
            label="Zeitraum"
            items={statisticsPeriods.map((value) => ({ value, label: statisticsPeriodLabels[value] }))}
            value={period}
            onChange={setPeriod}
          />
        </div>
      </div>

      {statistics.isError ? (
        <LoadError
          error={statistics.error}
          what="Die Statistik"
          retrying={statistics.isFetching}
          onRetry={() => void statistics.refetch()}
        />
      ) : null}

      <div className={styles.kpis}>
        {(data
          ? [
              ['Anwahlen', formatInteger(data.kpis.dials)],
              ['Gespräche', formatInteger(data.kpis.conversations)],
              ['Gesprächszeit', data.kpis.talkLabel],
              ['Termine gebucht', formatInteger(data.kpis.appointments)],
              [
                'Wahrgenommen',
                `${formatInteger(data.kpis.attended)} · ${formatInteger(data.kpis.attendedPct)} %`,
              ],
            ]
          : [['Anwahlen'], ['Gespräche'], ['Gesprächszeit'], ['Termine gebucht'], ['Wahrgenommen']]
        ).map(([label, value]) => (
          <div key={label} className={styles.kpi}>
            <div className={styles.kpiLabel}>{label}</div>
            <div className={styles.kpiValue}>{value ?? <Skeleton width={70} height={24} />}</div>
          </div>
        ))}
      </div>

      <div className={styles.columns}>
        <section className={styles.card} aria-labelledby="dialer-statistics-results">
          <h2 id="dialer-statistics-results" className={styles.cardTitle}>
            Ergebnisse je Gespräch
          </h2>
          <div className={styles.rows}>
            {data
              ? data.results.map((row) => (
                  <div key={row.key} className={styles.row}>
                    <span className={styles.rowLabel}>{row.label}</span>
                    <span
                      className={styles.bar}
                      role="img"
                      aria-label={`${row.label}: ${formatInteger(row.pct)} % der ${row.basis === 'dials' ? 'Anwahlen' : 'Gespräche'}`}
                    >
                      <span className={styles.barFill} style={{ width: `${barWidth(row, data.kpis)}%` }} />
                    </span>
                    <span className={styles.rowValue}>{formatInteger(row.count)}</span>
                  </div>
                ))
              : Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className={styles.row}>
                    <Skeleton width={110} height={13} />
                    <Skeleton width="100%" height={22} />
                    <span />
                  </div>
                ))}
          </div>
        </section>
        <div className={styles.side}>
          <section className={styles.sideCard} aria-labelledby="dialer-statistics-lists">
            <div id="dialer-statistics-lists" className={styles.eyebrow}>
              Je Liste
            </div>
            <div className={styles.lists}>
              {data && data.perList.length === 0 ? (
                <div className={styles.listRow}>
                  <span className={styles.listName}>Noch keine Termine in diesem Zeitraum.</span>
                </div>
              ) : null}
              {data?.perList.map((row) => (
                <div key={row.listId ?? 'record'} className={styles.listRow}>
                  <span className={styles.listName}>{row.name}</span>
                  <span className={styles.listValue}>
                    {formatInteger(row.appointments)} {row.appointments === 1 ? 'Termin' : 'Termine'}
                  </span>
                </div>
              ))}
            </div>
          </section>
          <div className={styles.hint}>Teamzahlen sehen nur Nutzer mit Führungsrecht über diese Setter.</div>
        </div>
      </div>
    </div>
  );
}
